import { ChevronRight, FileText, Folder, FolderOpen } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";

import { cn } from "@/lib/utils";
import type { WikiPageTreeNode } from "@/services/types/matter-wiki-types";

interface WikiSidebarProps {
  tree: WikiPageTreeNode[];
  activePath: string;
  onSelectPage: (path: string) => void;
}

interface TreeNodeProps {
  node: WikiPageTreeNode;
  depth: number;
  activePath: string;
  expandedPaths: Set<string>;
  onToggleExpand: (path: string) => void;
  onSelectPage: (path: string) => void;
}

function TreeNode({
  node,
  depth,
  activePath,
  expandedPaths,
  onToggleExpand,
  onSelectPage,
}: TreeNodeProps) {
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedPaths.has(node.path);
  const isActive = node.path === activePath;
  const isLeaf = node.page_id !== null && !hasChildren;
  const isSectionHeader = depth === 0 && !node.page_id && hasChildren;

  // Check if any descendant is active (for parent highlighting)
  const isAncestorOfActive = activePath.startsWith(`${node.path}/`);

  const handleClick = useCallback(() => {
    if (hasChildren) {
      onToggleExpand(node.path);
    }
    if (node.page_id) {
      onSelectPage(node.path);
    }
  }, [hasChildren, node.page_id, node.path, onToggleExpand, onSelectPage]);

  if (isSectionHeader) {
    return (
      <div>
        <button
          type="button"
          onClick={handleClick}
          className={cn(
            "flex w-full items-center gap-1.5 py-2 px-3 cursor-pointer",
            "text-[11px] uppercase tracking-wider font-medium",
            "transition-colors duration-150",
            isAncestorOfActive ? "text-zinc-600" : "text-zinc-400",
            "hover:text-zinc-500",
          )}
          style={{ paddingLeft: `${depth * 16 + 12}px` }}
        >
          <ChevronRight
            className={cn(
              "h-3 w-3 text-zinc-300 shrink-0",
              "transition-transform duration-200",
              isExpanded && "rotate-90",
            )}
          />
          <span className="truncate">{node.title}</span>
        </button>

        {isExpanded && (
          <div>
            {node.children.map((child) => (
              <TreeNode
                key={child.path}
                node={child}
                depth={depth + 1}
                activePath={activePath}
                expandedPaths={expandedPaths}
                onToggleExpand={onToggleExpand}
                onSelectPage={onSelectPage}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={handleClick}
        className={cn(
          "flex w-full items-center gap-2 py-1.5 px-3 text-sm cursor-pointer rounded-md",
          "transition-all duration-200",
          "text-zinc-500",
          "hover:bg-zinc-50 hover:text-zinc-700",
          isActive && "bg-zinc-900/5 text-zinc-900 font-medium",
        )}
        style={{ paddingLeft: `${depth * 16 + 12}px` }}
      >
        {hasChildren && (
          <ChevronRight
            className={cn(
              "h-3 w-3 text-zinc-300 shrink-0",
              "transition-transform duration-200",
              isExpanded && "rotate-90",
            )}
          />
        )}

        {isLeaf ? (
          <FileText className="h-3.5 w-3.5 text-zinc-300 shrink-0" />
        ) : isExpanded ? (
          <FolderOpen className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
        ) : (
          <Folder className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
        )}

        <span className="truncate">{node.title}</span>
      </button>

      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={child.path}
              node={child}
              depth={depth + 1}
              activePath={activePath}
              expandedPaths={expandedPaths}
              onToggleExpand={onToggleExpand}
              onSelectPage={onSelectPage}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function collectAllPaths(nodes: WikiPageTreeNode[]): string[] {
  return nodes.reduce<string[]>((paths, node) => {
    if (node.children.length > 0) {
      return [...paths, node.path, ...collectAllPaths(node.children)];
    }
    return paths;
  }, []);
}

export const WikiSidebar: React.FC<WikiSidebarProps> = ({
  tree,
  activePath,
  onSelectPage,
}) => {
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(() => {
    return new Set(collectAllPaths(tree));
  });

  // Auto-expand parent paths when active path changes
  useEffect(() => {
    if (!activePath) return;
    const parts = activePath.split("/").filter(Boolean);
    const parentPaths = parts
      .slice(0, -1)
      .map((_, idx) => `/${parts.slice(0, idx + 1).join("/")}`);
    if (parentPaths.length > 0) {
      setExpandedPaths((prev) => {
        const missing = parentPaths.filter((p) => !prev.has(p));
        if (missing.length === 0) return prev;
        const next = new Set(prev);
        missing.forEach((p) => next.add(p));
        return next;
      });
    }
  }, [activePath]);

  const handleToggleExpand = useCallback((path: string) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  return (
    <nav
      className={cn(
        "h-full py-3 overflow-y-auto scroll-smooth",
        "[&::-webkit-scrollbar]:w-1",
        "[&::-webkit-scrollbar-track]:bg-transparent",
        "[&::-webkit-scrollbar-thumb]:bg-zinc-200 [&::-webkit-scrollbar-thumb]:rounded-full",
        "[&::-webkit-scrollbar]:opacity-0 hover:[&::-webkit-scrollbar]:opacity-100",
        "[&::-webkit-scrollbar-thumb]:transition-opacity [&::-webkit-scrollbar-thumb]:duration-200",
      )}
    >
      {tree.map((node) => (
        <TreeNode
          key={node.path}
          node={node}
          depth={0}
          activePath={activePath}
          expandedPaths={expandedPaths}
          onToggleExpand={handleToggleExpand}
          onSelectPage={onSelectPage}
        />
      ))}
    </nav>
  );
};
